import { FlexColumn, LabeledData, MaterialIcons, useMoment } from '@gorila-shared-ui/components';
import { StatefulPopover } from 'baseui/popover';
import { LabelMedium, LabelSmall, ParagraphSmall } from 'baseui/typography';
import { memo } from 'react';
import { Marker } from 'react-map-gl/mapbox';
import { useRecoilValue } from 'recoil';
import { useCamera } from '../../../hooks/useCamera';
import { useStyles } from '../../../hooks/useStyles';
import { assetState } from '../../../storage/assets';
import { gptAlarmTypeIconState, gptAlarmTypeState } from '../../../storage/gptStates';
import { HistoricAlarm } from '../../../types/currentState';
import { getAssetTitle } from '../../../utils/assets';
import { isValidSafegptAlarmPoint } from '../../../utils/map';
import AlarmsReportEventType from '../../reports/alarmsReport/AlarmsReportEventType';
import SafeGptAlarmIcon from '../../shared/SafeGptAlarmIcon';

type Props = {
  alarm: HistoricAlarm;
};

function ReportAlarmMarker({ alarm }: Readonly<Props>) {
  const { css, theme } = useStyles();
  const { formatDateFromTs } = useMoment();
  const { zoomTo } = useCamera();
  const asset = useRecoilValue(assetState(alarm.assetId));
  const alarmData = useRecoilValue(gptAlarmTypeState(alarm.alarmType));
  const alarmIcon = useRecoilValue(gptAlarmTypeIconState(alarm.alarmType));
  const labelWidth = '95px';
  const iconWrapper = css({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: 0,
    height: '25px',
    width: '25px',
    borderRadius: '50%',
    background: alarmData?.color || '#B00000',
    paddingBottom: '2px',
    boxSizing: 'border-box',
  });
  if (!alarm) return null;

  if (!isValidSafegptAlarmPoint(alarm.gpsLng, alarm.gpsLat)) return null;

  const onClick = () => {
    zoomTo({
      lat: alarm.gpsLat,
      lng: alarm.gpsLng,
    });
  };

  return (
    <Marker
      longitude={alarm.gpsLng}
      latitude={alarm.gpsLat}
      onClick={onClick}
    >
      <StatefulPopover
        showArrow
        autoFocus={false}
        returnFocus
        placement="top"
        triggerType="hover"
        popoverMargin={0}
        content={() => (
          <FlexColumn
            gap={theme.sizing.scale300}
            classNames={css({
              paddingRight: theme.sizing.scale600,
              paddingLeft: theme.sizing.scale600,
              paddingTop: theme.sizing.scale500,
              paddingBottom: theme.sizing.scale500,
            })}
          >
            {asset && (
              <FlexColumn gap={0}>
                <LabelMedium>{getAssetTitle(asset)}</LabelMedium>
                <LabelSmall color={theme.colors.contentSecondary}>{asset.label}</LabelSmall>
              </FlexColumn>
            )}

            <FlexColumn gap={theme.sizing.scale0}>
              <LabeledData
                label="Alarma:"
                content={
                  <ParagraphSmall
                    margin={0}
                    color={theme.colors.contentSecondary}
                  >
                    <AlarmsReportEventType code={alarm.alarmType} />
                  </ParagraphSmall>
                }
                labelWidth={labelWidth}
              />
              <LabeledData
                label="Fecha:"
                content={formatDateFromTs(+alarm.gpsTime)}
                labelWidth={labelWidth}
              />
              <LabeledData
                label="Coordenadas:"
                content={`${alarm.gpsLat}, ${alarm.gpsLng}`}
                maxWidth="300px"
                inline={false}
                maxLines={5}
                labelWidth={labelWidth}
              />
            </FlexColumn>
          </FlexColumn>
        )}
        overrides={{
          Inner: {
            style: {
              backgroundColor: theme.colors.backgroundPrimary,
            },
          },
          Arrow: {
            style: {
              backgroundColor: theme.colors.backgroundPrimary,
            },
          },
        }}
      >
        <span className={iconWrapper}>
          {alarmIcon && (
            <SafeGptAlarmIcon
              url={alarmIcon}
              size="16px"
            />
          )}
          {!alarmIcon && (
            <MaterialIcons.Warning
              size="16px"
              color="#FFF"
            />
          )}
        </span>
      </StatefulPopover>
    </Marker>
  );
}

export default memo(ReportAlarmMarker);
